import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, HandCoins, Mail, X, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import logo from "@/assets/galahadji-logo.png";
import heroEmblem from "@/assets/galahadji-emblem.png";

const HERO = "https://images.pexels.com/photos/8082322/pexels-photo-8082322.jpeg";
const TEAM = "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg";

export default function Index() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-white/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="header-logo"><img src={logo} alt="Galahadji Acquisitions" className="h-14 w-auto object-contain" /></Link>
          </div>
          <nav className="flex items-center gap-6 text-sm">
            <a href="#how" className="hidden sm:inline text-muted-foreground hover:text-foreground transition-colors">How it works</a>
            <Link to="/pricing" data-testid="header-pricing-link" className="hidden sm:inline text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
            <Link to="/login" data-testid="header-login-link" className="text-muted-foreground hover:text-foreground transition-colors">Team Login</Link>
            <Link to="/seller-intake" data-testid="header-sell-cta" className="bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">Sell Your Property</Link>
          </nav>
        </div>
      </header>

      <section className="relative min-h-screen flex items-center grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Luxury property" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-28 pb-24 w-full">
          <motion.div initial={{ opacity: 0, y: -20, scale: 0.94 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.8 }} className="flex flex-col items-center mb-10">
            <img src={heroEmblem} alt="Galahadji" className="h-40 sm:h-52 lg:h-60 w-auto object-contain logo-3d" data-testid="hero-main-logo" />
            <div className="mt-5 text-center">
              <div className="flex items-center justify-center gap-3">
                <span className="h-px w-12 bg-white/70"></span>
                <span className="text-white font-semibold tracking-[0.45em] text-lg sm:text-xl uppercase" data-testid="hero-logo-acquisitions">Acquisitions</span>
                <span className="h-px w-12 bg-white/70"></span>
              </div>
              <div className="text-white tracking-[0.28em] text-xs sm:text-sm uppercase mt-3 font-medium" data-testid="hero-logo-tagline">Acquire · Transform · Build Wealth</div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="max-w-3xl mx-auto text-center">
            <span className="text-accent uppercase tracking-[0.3em] text-xs font-medium">United States · Real Estate Acquisitions</span>
            <h1 className="font-marketing text-5xl sm:text-6xl lg:text-7xl font-bold text-white mt-6 leading-[0.95]">Sell your home on your terms — <span className="italic text-accent">certainty, speed, discretion.</span></h1>
            <p className="text-white/80 text-lg mt-8 max-w-xl mx-auto leading-relaxed">Galahadji acquires properties nationwide with flexible structures: cash, subject-to, and seller financing. No repairs, no listings, no uncertainty.</p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link to="/seller-intake" data-testid="hero-sell-cta" className="group bg-accent text-accent-foreground px-7 py-3.5 rounded-md font-semibold flex items-center gap-2 hover:bg-accent/90 transition-colors">
                Get Your Offer <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/login" data-testid="hero-login-cta" className="border border-white/30 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white/10 transition-colors">Team Portal</Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <span className="text-accent uppercase tracking-[0.25em] text-xs font-medium">How it works</span>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 mb-14 max-w-2xl">A structured acquisition process built for sellers who value time.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: HandCoins, t: "Submit your property", d: "Share a few details on your home and situation. Takes under three minutes." },
            { icon: Clock, t: "Receive a tailored offer", d: "Our underwriters analyze cash, subject-to, and seller-finance structures for you." },
            { icon: ShieldCheck, t: "Close with confidence", d: "Pick your closing date. We handle the paperwork and coordinate every detail." },
          ].map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-card border border-border rounded-md p-8 hover:-translate-y-1 hover:shadow-sm transition-transform duration-200">
              <s.icon className="w-8 h-8 text-accent" />
              <div className="text-5xl font-display font-bold text-border mt-4">0{i + 1}</div>
              <h3 className="font-display text-xl font-bold mt-2">{s.t}</h3>
              <p className="text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="firm" className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-accent uppercase tracking-[0.25em] text-xs font-medium">The Firm</span>
            <h2 className="font-marketing text-4xl sm:text-5xl font-bold mt-4 leading-tight">Institutional discipline. <span className="italic text-accent">Boutique attention.</span></h2>
            <p className="text-white/70 mt-6 leading-relaxed">We combine rigorous deal analysis with a genuine commitment to sellers. Every property is underwritten across multiple strategies to arrive at the fairest possible structure.</p>
            <div className="grid grid-cols-3 gap-6 mt-10">
              {[["$120M+", "Acquired"], ["600+", "Closings"], ["14 days", "Avg. close"]].map(([n, l]) => (
                <div key={l}><div className="font-display text-3xl font-bold text-accent tabnum">{n}</div><div className="text-white/50 text-xs uppercase tracking-wider mt-1">{l}</div></div>
              ))}
            </div>
          </div>
          <div className="relative rounded-md overflow-hidden"><img src={TEAM} alt="Team" className="w-full h-[420px] object-cover" /></div>
        </div>
      </section>

      <section id="work-with-us" className="max-w-7xl mx-auto px-6 py-24">
        <span className="text-accent uppercase tracking-[0.25em] text-xs font-medium">Work With Us</span>
        <h2 className="font-marketing text-4xl sm:text-5xl font-bold mt-4">Two ways to <span className="italic text-accent">get started.</span></h2>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <PublicFormCard
            title="Request a Cash Offer"
            desc="Selling a property? Tell us about it and receive a no-obligation cash offer — no repairs, no listings, close on your timeline."
            cta="Get my cash offer"
            testid="cash-offer-card"
            source="cash-offer-form"
            interest="Cash offer request"
            fields={[
              { key: "property_address", label: "Property address", placeholder: "123 Main St, Las Vegas, NV" },
              { key: "timeframe", label: "How soon do you want to sell?", type: "select", options: ["As soon as possible", "Within 30 days", "Within 60 days", "Just exploring"] },
              { key: "price_expectation", label: "Price expectation (optional)", placeholder: "$350,000" },
            ]} />
          <PublicFormCard
            title="Buyer / Investor Inquiry"
            desc="Looking for off-market deals, flips, or JV opportunities? Tell us what you buy and we'll bring you matching inventory."
            cta="Send investor inquiry"
            testid="investor-card"
            source="investor-form"
            interest="Buyer / Investor inquiry"
            fields={[
              { key: "markets", label: "Markets you buy in", placeholder: "Las Vegas, Phoenix…" },
              { key: "budget", label: "Budget / buying power", placeholder: "$200k–$500k" },
              { key: "strategy", label: "Strategy", type: "select", options: ["Fix & Flip", "Buy & Hold", "Subject-To / Creative", "Wholesale", "Any"] },
            ]} />
        </div>
      </section>

      <footer className="bg-primary text-white/50 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4 text-sm">
          <div className="flex items-center gap-2"><span>Galahadji Acquisitions Inc · Las Vegas, NV</span></div>
          <div>© {new Date().getFullYear()} Galahadji. All rights reserved.</div>
        </div>
      </footer>
      <ContactPopup />
    </div>
  );
}

function PublicFormCard({ title, desc, cta, source, interest, fields, testid }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full border border-border rounded-md px-3 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/40";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) { toast.error("Please enter a valid email."); return; }
    setBusy(true);
    try {
      await api.post("/public/contact", { ...form, source, interest });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="bg-card border border-border rounded-md p-8 flex flex-col hover:-translate-y-1 hover:shadow-sm transition-transform duration-200" data-testid={testid}>
        <h3 className="font-display text-2xl font-bold">{title}</h3>
        <p className="text-muted-foreground mt-3 leading-relaxed flex-1">{desc}</p>
        <button onClick={() => { setDone(false); setOpen(true); }} data-testid={`${testid}-open`}
          className="mt-6 self-start bg-accent text-accent-foreground px-6 py-3 rounded-md font-semibold hover:bg-accent/90 transition-colors">{cta}</button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 10 }}
              className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
              <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
              <div className="bg-primary text-primary-foreground px-6 pt-6 pb-5">
                <h3 className="font-marketing text-2xl font-bold leading-tight">{title}</h3>
                <p className="text-white/70 text-sm mt-2">{desc}</p>
              </div>
              {done ? (
                <div className="p-8 text-center" data-testid={`${testid}-success`}>
                  <div className="mx-auto w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center"><Send className="w-5 h-5 text-accent" /></div>
                  <h4 className="font-display font-bold text-lg mt-4">Thank you!</h4>
                  <p className="text-muted-foreground text-sm mt-1">We've received your details and will be in touch within one business day.</p>
                  <button onClick={() => setOpen(false)} className="mt-5 bg-primary text-primary-foreground px-5 py-2.5 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">Close</button>
                </div>
              ) : (
                <form onSubmit={submit} className="p-6 space-y-3" data-testid={`${testid}-form`}>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Full name</label>
                    <input value={form.name} onChange={set("name")} className={inputCls} data-testid={`${testid}-name`} /></div>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Email <span className="text-accent">*</span></label>
                    <input type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid={`${testid}-email`} /></div>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Phone</label>
                    <input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid={`${testid}-phone`} /></div>
                  {fields.map((f) => (
                    <div key={f.key} className="space-y-1">
                      <label className="text-xs text-muted-foreground">{f.label}</label>
                      {f.type === "select"
                        ? <select value={form[f.key] || ""} onChange={set(f.key)} className={inputCls} data-testid={`${testid}-${f.key}`}>
                            <option value="">Select…</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        : <input value={form[f.key] || ""} onChange={set(f.key)} placeholder={f.placeholder || ""} className={inputCls} data-testid={`${testid}-${f.key}`} />}
                    </div>
                  ))}
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Anything else?</label>
                    <textarea rows={2} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid={`${testid}-message`} /></div>
                  <button type="submit" disabled={busy} data-testid={`${testid}-submit`}
                    className="w-full bg-accent text-accent-foreground py-3 rounded-md font-semibold flex items-center justify-center gap-2 hover:bg-accent/90 transition-colors disabled:opacity-60">
                    {busy ? "Sending…" : <>{cta} <Send className="w-4 h-4" /></>}
                  </button>
                  <p className="text-[11px] text-muted-foreground text-center">We respect your privacy. Your details are only used to contact you.</p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function ContactPopup() {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", interest: "Join the Subject-To community (membership)" });

  useEffect(() => {
    if (sessionStorage.getItem("gh_contact_popup_seen")) return;
    const t = setTimeout(() => setOpen(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const close = () => { setOpen(false); sessionStorage.setItem("gh_contact_popup_seen", "1"); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const INTERESTS = [
    "Join the Subject-To community (membership)",
    "Buy, flip & sell properties",
    "Sell my property",
    "General enquiry",
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) { toast.error("Please enter a valid email."); return; }
    setBusy(true);
    try {
      await api.post("/public/contact", { ...form, source: "landing-popup" });
      setDone(true);
      sessionStorage.setItem("gh_contact_popup_seen", "1");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating re-open button */}
      <button onClick={() => { setDone(false); setOpen(true); }} data-testid="contact-fab"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-accent text-accent-foreground px-5 py-3 rounded-full font-semibold shadow-lg hover:bg-accent/90 hover:-translate-y-0.5 transition-all">
        <Mail className="w-4 h-4" /> Contact us
      </button>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" onClick={close} data-testid="contact-overlay" />
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-2xl overflow-hidden" data-testid="contact-popup">
              <button onClick={close} data-testid="contact-close" className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"><X className="w-5 h-5" /></button>
              <div className="bg-primary text-primary-foreground px-6 pt-6 pb-5">
                <span className="text-accent uppercase tracking-[0.25em] text-[11px] font-medium">Get in touch</span>
                <h3 className="font-marketing text-2xl font-bold mt-1 leading-tight">Join the community or sell your property.</h3>
                <p className="text-white/70 text-sm mt-2">Whether you invest — buying, flipping and selling with Subject-To — or you're a homeowner ready to sell, leave your details and our team will reach out within one business day.</p>
              </div>

              {done ? (
                <div className="p-8 text-center" data-testid="contact-success">
                  <div className="mx-auto w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center"><Send className="w-5 h-5 text-accent" /></div>
                  <h4 className="font-display font-bold text-lg mt-4">Thank you!</h4>
                  <p className="text-muted-foreground text-sm mt-1">We've received your details and will be in touch shortly.</p>
                  <button onClick={close} data-testid="contact-success-close" className="mt-5 bg-primary text-primary-foreground px-5 py-2.5 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">Close</button>
                </div>
              ) : (
                <form onSubmit={submit} className="p-6 space-y-3" data-testid="contact-form">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">I'm interested in <span className="text-accent">*</span></label>
                    <select value={form.interest} onChange={set("interest")} data-testid="contact-interest"
                      className="w-full border border-border rounded-md px-3 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/40">
                      {INTERESTS.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Full name</label>
                    <input value={form.name} onChange={set("name")} data-testid="contact-name" placeholder="Jane Doe"
                      className="w-full border border-border rounded-md px-3 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/40" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Email <span className="text-accent">*</span></label>
                    <input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" placeholder="you@email.com"
                      className="w-full border border-border rounded-md px-3 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/40" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Phone</label>
                    <input type="tel" value={form.phone} onChange={set("phone")} data-testid="contact-phone" placeholder="(555) 123-4567"
                      className="w-full border border-border rounded-md px-3 py-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/40" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">How can we help?</label>
                    <textarea value={form.message} onChange={set("message")} data-testid="contact-message" rows={3} placeholder="Tell us about your goals — your property, or the kind of deals you want to do…"
                      className="w-full border border-border rounded-md px-3 py-2.5 bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent/40" />
                  </div>
                  <button type="submit" disabled={busy} data-testid="contact-submit"
                    className="w-full bg-accent text-accent-foreground py-3 rounded-md font-semibold flex items-center justify-center gap-2 hover:bg-accent/90 transition-colors disabled:opacity-60">
                    {busy ? "Sending…" : <>Send message <Send className="w-4 h-4" /></>}
                  </button>
                  <p className="text-[11px] text-muted-foreground text-center">We respect your privacy. Your details are only used to contact you.</p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
